/**
 * HomePage — ActanonEdu marketing home.
 */
import { Link } from 'react-router-dom';
import MarketingLayout from './shared/MarketingLayout';
import { Section, SectionHeader, FeatureCard, CTAStrip, FAQAccordion, LogoStrip } from './shared/blocks';
import { NewsletterSignup } from './shared/PremiumBlocks';
import ExperienceBoscoCaptureDemo from './shared/ExperienceBoscoCaptureDemo';
import SEO from './shared/SEO';
import { FAQ_ITEMS } from './content';
import { HomeHero } from './home/HomeHero';
import { ProductShowcase } from './home/ProductShowcase';
import { BoscoSection } from './home/BoscoSection';
import { SchoolSection } from './home/SchoolSection';
import { CAPABILITIES, TOOLS, STEPS, BOSCO_FAQ } from './home/homeContent';

const HOME_FAQ = [...BOSCO_FAQ, ...FAQ_ITEMS];

function CapabilityStrip() {
  return (
    <section className="bg-white dark:bg-mk-midnight border-b border-mk-line dark:border-mk-edge" aria-label="Capabilities" data-testid="capability-strip">
      <ul className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4">
        {CAPABILITIES.map(({ icon: Icon, title, text }, i) => (
          <li key={title} className={`flex items-start gap-3 py-7 sm:py-9 px-2 sm:px-5 ${i > 0 ? 'lg:border-l border-mk-line dark:border-mk-edge' : ''}`} data-testid={`capability-item-${i}`}>
            <Icon className="h-5 w-5 mt-0.5 text-mk-blue shrink-0" aria-hidden="true" />
            <div>
              <div className="font-heading font-bold text-mk-ink dark:text-white">{title}</div>
              <div className="text-sm text-mk-muted dark:text-slate-400 mt-0.5">{text}</div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ToolsGrid() {
  return (
    <Section id="teacher-tools" tone="muted" data-testid="feature-grid">
      <SectionHeader eyebrow="AI teaching tools" title="Six tools. Every document a teacher needs." />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {TOOLS.map((t, i) => <FeatureCard key={t.name} icon={t.icon} name={t.name} summary={t.summary} testid={`feature-card-${i}`} />)}
      </div>
    </Section>
  );
}

function HowItWorks() {
  return (
    <Section id="how-it-works" data-testid="how-it-works-section">
      <SectionHeader eyebrow="How it works" title="From idea to classroom in four steps." />
      <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {STEPS.map((s, i) => (
          <li key={s.title} className="relative rounded-2xl border border-mk-line dark:border-mk-edge p-6 bg-white dark:bg-mk-navy" data-testid={`how-it-works-step-${i + 1}`}>
            <span className="font-heading text-sm font-extrabold text-mk-blue">0{i + 1}</span>
            <h3 className="mt-3 text-lg font-bold text-mk-ink dark:text-white">{s.title}</h3>
            <p className="mt-1.5 text-sm text-mk-muted dark:text-slate-300">{s.text}</p>
          </li>
        ))}
      </ol>
    </Section>
  );
}

export default function HomePage() {
  return (
    <MarketingLayout>
      <SEO
        title="ActanonEdu | AI Teaching Tools & Academic Intelligence"
        description="Discover ActanonEdu, an AI-powered education platform for teachers and schools. Create lesson plans, assessments and teaching resources with BOSCO AI."
        path="/"
        breadcrumbs={[{ name: 'Home', url: '/' }]}
        faq={HOME_FAQ}
      />
      <HomeHero />
      <CapabilityStrip />
      <ProductShowcase />
      <BoscoSection />
      <ExperienceBoscoCaptureDemo />
      <ToolsGrid />
      <LogoStrip labels={['CBSE', 'ICSE', 'IGCSE', 'IB MYP/DP', 'CIS', 'State Boards']} />
      <SchoolSection />
      <HowItWorks />
      <Section id="faq" tone="muted">
        <div className="grid lg:grid-cols-[1fr_1.6fr] gap-10">
          <SectionHeader eyebrow="FAQ" title="Questions, answered." subtitle={<>Still curious? <Link to="/contact" className="font-semibold text-mk-deep dark:text-mk-cyan underline underline-offset-4" data-testid="home-faq-contact">Contact us</Link>.</>} />
          <FAQAccordion items={HOME_FAQ} testid="home-faq" />
        </div>
      </Section>
      <Section><NewsletterSignup /></Section>
      <CTAStrip
        title="Teach smarter, starting today."
        subtitle="Free starter plan. No credit card."
        primary={{ label: 'Get Started', href: '/signup' }}
        secondary={{ label: 'Chat with us', wa: 'Hi! I came from the ActanonEdu home page.' }}
        testid="home-final-cta"
      />
    </MarketingLayout>
  );
}
